/** コード列 (code-list-string) の既定区切り文字。 */
export const DEFAULT_CODE_SEPARATOR = ',';
/** キーが「コード列」を示すか (`code` / `codes` / `Code` / `Codes` で終わる)。 */
const CODE_KEY_RE = /(?:^|[_-])codes?$|[a-z0-9]Codes?$/;
/** `sub_gql` / `subGql` のような「従属軸」キーか。 */
const SUB_KEY_RE = /^sub[_-]?(.+)$/;
/**
 * 軸 root の正規化 (照合用)。
 *   `category_codes` → `category` / `emotions` → `emotion` / `gql_code` → `gql`
 *   `categoryMaster` の root `category` → `category`
 * 末尾の code/codes を落とし、区切り記号を除去し、小文字化し、最後に複数形の `s` を落とす。
 */
export function normalizeAxisRoot(key) {
    const withoutCode = key.replace(/(?:[_-])?[Cc]odes?$/, '');
    const base = (withoutCode || key).replace(/[_-]/g, '').toLowerCase();
    if (base.length > 1 && base.endsWith('s'))
        return base.slice(0, -1);
    return base;
}
/**
 * 出力フィールドのキーから、対応する M2 マスタの `templateVar` を推定する。
 * `category_codes` → `categoryMaster` / `emotions` → `emotionMaster` /
 * `gql_code` → `gqlMaster` / `sub_gql` → `gqlMaster` (従属軸は親軸のマスタ)。
 */
export function masterTemplateVarForKey(key) {
    const sub = SUB_KEY_RE.exec(key);
    const base = sub?.[1] ? sub[1] : key;
    return `${normalizeAxisRoot(base)}Master`;
}
/** fields[] の 1 要素が軸になりうるかを判定し、区切り文字を返す。 */
function axisSpecOfField(field) {
    switch (field.kind) {
        case 'code-list-string':
            return { separator: field.separator || DEFAULT_CODE_SEPARATOR, multi: true };
        case 'enum-array':
            return { separator: DEFAULT_CODE_SEPARATOR, multi: true };
        case 'text':
            return CODE_KEY_RE.test(field.key)
                ? { separator: DEFAULT_CODE_SEPARATOR, multi: false }
                : null;
        default:
            // object-array (classifications 等) は「分類の入れ物」なので軸そのものではない。
            return null;
    }
}
/**
 * `schema.fields[]` から分類軸を導出する (モード非依存)。
 * `classificationAxes` が明示されているフォーマットでは呼ばれない (そちらが正)。
 */
export function deriveAxesFromFields(schema) {
    const fields = schema?.fields ?? [];
    const candidates = [];
    for (const field of fields) {
        const spec = axisSpecOfField(field);
        if (!spec)
            continue;
        const root = normalizeAxisRoot(field.key);
        if (candidates.some((c) => c.root === root))
            continue;
        candidates.push({
            kind: field.kind,
            root,
            separator: spec.separator,
            multi: spec.multi,
            explicit: false,
            primary: false,
            axis: {
                key: root,
                codeField: field.key,
                nameField: '',
                label: ('label' in field && field.label) || field.key,
                majorCodeExtract: 'before-hyphen',
            },
        });
    }
    // `sub_*` は親軸 (root 一致) の subField として吸収する (例 sub_gql ↔ gql_code)。
    const axes = [];
    for (const cand of candidates) {
        const sub = SUB_KEY_RE.exec(cand.axis.codeField);
        const parentRoot = sub?.[1] ? normalizeAxisRoot(sub[1]) : null;
        const parent = parentRoot ? candidates.find((c) => c.root === parentRoot && c !== cand) : null;
        if (parent) {
            parent.axis.subField = cand.axis.codeField;
            continue;
        }
        axes.push(cand);
    }
    if (axes.length === 0)
        return [];
    // 主軸 = 最初の `code-list-string` (= AI が認識した対象物のコード列)。無ければ先頭。
    // 「分類 1 件 = 1 行」の行数はこの軸の件数で決まるため、複数コードを持つのが自然な
    // code-list-string を優先する (感情のような付随ラベル軸で行数が決まるのを避ける)。
    const primaryIdx = Math.max(0, axes.findIndex((a) => a.kind === 'code-list-string'));
    return axes.map((a, i) => {
        const { kind: _kind, ...rest } = a;
        void _kind;
        return {
            ...rest,
            primary: i === primaryIdx,
            axis: { ...rest.axis, role: i === primaryIdx ? 'primary' : 'secondary' },
        };
    });
}
/**
 * 出力フォーマットの分類軸を解決する。
 * `classificationAxes` があればそれを、無ければ `fields[]` から導出する。
 */
export function resolveFormatAxes(schema) {
    const explicit = schema?.classificationAxes ?? [];
    if (explicit.length > 0) {
        const primaryIdx = Math.max(0, explicit.findIndex((a) => a.role === 'primary'));
        return explicit.map((axis, i) => ({
            axis,
            root: normalizeAxisRoot(axis.key ?? axis.codeField ?? axis.nameField ?? ''),
            separator: DEFAULT_CODE_SEPARATOR,
            multi: true,
            explicit: true,
            primary: i === primaryIdx,
        }));
    }
    return deriveAxesFromFields(schema);
}
/**
 * M2 カテゴリマスタの rows[] から、AI 出力コードに対応する行を探す (モード非依存)。
 *
 * AI 出力のコード表記はテンプレート次第で揺れる。マスタ側は `majorCd` + `categoryCd` の
 * 2 列なので、次の順で照合する:
 *   1. `categoryCd` 完全一致 (例 "01")
 *   2. 結合形 `${majorCd}-${categoryCd}` 一致 (例 "A-01" — ECW 対象物分類)
 *   3. **接頭辞付きコード** (例 "GQL-05")。最後の `-` 以降が `categoryCd` と一致する行が
 *      master 内で **一意** のときだけ採用する。GQL のように「マスタは 01〜13、出力は GQL-05」と
 *      ずれるテンプレートを、マスタを書き換えずに解決するため。一意でない (ECW 対象物の "01" の
 *      ように複数 major に跨る) ときは誤解決を避けて未解決のままにする。
 */
export function findMasterRowByCode(master, code) {
    if (!master || !code)
        return undefined;
    const rows = master.rows ?? [];
    for (const row of rows) {
        if (row.categoryCd === code)
            return row;
        if (row.majorCd && row.categoryCd && `${row.majorCd}-${row.categoryCd}` === code)
            return row;
    }
    const sepIdx = code.lastIndexOf('-');
    if (sepIdx <= 0)
        return undefined;
    const suffix = code.slice(sepIdx + 1);
    if (!suffix)
        return undefined;
    const matches = rows.filter((r) => r.categoryCd === suffix);
    return matches.length === 1 ? matches[0] : undefined;
}
/** `category_codes` → `categoryCodes` (AI 出力 / 保存の表記ゆれ吸収)。 */
function snakeToCamel(key) {
    return key.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());
}
/** 解析結果から key の値を取り出す (snake_case / camelCase 両対応)。 */
export function readResultField(result, key) {
    if (!key)
        return undefined;
    if (result[key] !== undefined)
        return result[key];
    const camel = snakeToCamel(key);
    return camel === key ? undefined : result[camel];
}
/** 任意の値をコード配列へ正規化 (配列 / カンマ区切り文字列 / 単一値)。 */
export function splitCodeValue(value, separator = DEFAULT_CODE_SEPARATOR) {
    if (value === null || value === undefined)
        return [];
    if (Array.isArray(value))
        return value.flatMap((v) => splitCodeValue(v, separator));
    if (typeof value === 'number')
        return [String(value)];
    if (typeof value !== 'string')
        return [];
    return value
        .split(separator)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}
/** 軸の値が入っているフィールド名 (codeField 優先、空なら nameField)。 */
export function axisValueField(axis) {
    return axis.codeField || axis.nameField || '';
}
/** 解析結果 (フラット) から 1 軸のコード集合を読む。 */
export function readAxisCodes(result, resolved) {
    if (!result)
        return [];
    const field = axisValueField(resolved.axis);
    if (!field)
        return [];
    return splitCodeValue(readResultField(result, field), resolved.separator);
}
/** 解析結果 (フラット) から 1 軸の従属コード集合 (subField) を読む。 */
export function readAxisSubCodes(result, resolved) {
    if (!result || !resolved.axis.subField)
        return [];
    return splitCodeValue(readResultField(result, resolved.axis.subField), resolved.separator);
}
/**
 * 解析結果で「分類セット (1 件 = 1 認識物)」を格納している配列フィールドの key を解決する。
 *
 * old2 以来 `classifications` 固定だったが、fields[] 駆動 (docs/105) では admin が任意の
 * キー名で object-array を定義できる。キー名をコードに焼かず、**出力フォーマットの
 * fields[] から引く** (見つからなければ従来の 'classifications')。
 */
export function resolveClassificationsKey(schema) {
    const arr = schema?.fields?.find((f) => f.kind === 'object-array');
    return arr?.key ?? 'classifications';
}
/**
 * **フラット構造の解析結果 → 分類セット (classifications 相当) への正規化**。
 *
 * ECW 系の出力は `classifications[]` を持たず、
 * `{ category_codes: "C-16,C-15,B-06", gql_code: "GQL-05", emotions: [...] }`
 * のようなフラット構造で保存される。この形のままでは「分類 1 件 = 1 行」を前提とする
 * 分析ツール (マトリクス / 一覧 / 地図) に 1 行も供給されず、投稿ごと丸ごと欠落する。
 *
 * 展開規則 (行の組み方):
 *   - **行数 = 主軸のコード件数** (主軸 = 最初の code-list-string、無ければ先頭軸)。
 *     ECW では「AI が認識した対象物」= `category_codes` が主軸になる。
 *   - 主軸以外の軸は、**各行に代表値 (先頭のコード) を載せる**。
 *     これにより「対象物 × ゲール基準」のような交差集計が、対象物の件数分だけ数えられる。
 *   - 直積 (3 対象物 × 3 感情 = 9 行) は採らない。1 投稿の重みが軸の数だけ増えてしまい、
 *     マトリクスの件数が実態から乖離するため。
 *   - `rank` は 1 始まりの連番、`reasoning` は `schema.reasoningField` の値。
 *
 * 軸の値が 1 つも無ければ空配列 (= 従来どおり「分類なし」)。
 */
export function classificationsFromFlatResult(result, schema) {
    if (!result)
        return [];
    const axes = resolveFormatAxes(schema);
    if (axes.length === 0)
        return [];
    // 分類セットを既に持つ結果は対象外 (呼出側が先に判定する想定だが二重防御)。
    const existing = readResultField(result, resolveClassificationsKey(schema));
    if (Array.isArray(existing) && existing.length > 0)
        return [];
    const values = axes.map((resolved) => ({
        resolved,
        codes: readAxisCodes(result, resolved),
        sub: readAxisSubCodes(result, resolved),
    }));
    if (values.every((v) => v.codes.length === 0 && v.sub.length === 0))
        return [];
    const primary = values.find((v) => v.resolved.primary) ?? values[0];
    const rowCount = Math.max(1, primary?.codes.length ?? 0);
    const reasoningField = schema?.reasoningField || 'reasoning';
    const reasoning = readResultField(result, reasoningField);
    const rows = [];
    for (let i = 0; i < rowCount; i++) {
        const row = { rank: i + 1 };
        for (const v of values) {
            const field = axisValueField(v.resolved.axis);
            if (!field)
                continue;
            // 主軸は i 番目。それ以外は **全値** を区切り文字で連結して保持する。
            // ここで代表値 (先頭) に切り詰めてしまうと、呼出側 (分析ツール) が「表示中の軸に
            // 並んだ複数値をそれぞれ別のデータとして扱う」ことができなくなるため。
            // 単一値として使いたい呼出側は splitCodeValue()[0] で先頭を取る。
            row[field] = v.resolved.primary ? (v.codes[i] ?? '') : v.codes.join(v.resolved.separator);
            if (v.resolved.axis.subField)
                row[v.resolved.axis.subField] = v.sub;
        }
        row[reasoningField] = typeof reasoning === 'string' ? reasoning : '';
        row.assessment = '';
        row.assessment_level = '';
        row.score = 0;
        rows.push(row);
    }
    return rows;
}
/**
 * 解析結果が「この出力フォーマットの fields[] に対応する値」を実際に持っているか。
 * 投稿詳細で「出力フォーマット駆動の表示」に切り替えてよいかの判定に使う
 * (空の `{classifications: []}` しか持たない旧投稿を誤って新表示に載せないため)。
 */
export function hasFormatFieldValues(result, schema) {
    if (!result)
        return false;
    const fields = schema?.fields ?? [];
    if (fields.length === 0)
        return false;
    const isNonEmpty = (v) => {
        if (v === null || v === undefined)
            return false;
        if (typeof v === 'string')
            return v.trim().length > 0;
        if (Array.isArray(v))
            return v.length > 0;
        if (typeof v === 'object')
            return Object.keys(v).length > 0;
        return true;
    };
    for (const field of fields) {
        if (isNonEmpty(readResultField(result, field.key)))
            return true;
        const reason = field.kind === 'enum-array' || field.kind === 'code-list-string'
            ? field.reasonField
            : undefined;
        if (reason && isNonEmpty(readResultField(result, reason.key)))
            return true;
    }
    return false;
}
//# sourceMappingURL=aiFormatAxes.js.map